import { type EngineEventMap } from '../core/EngineEvents';
import { type EventEmitter } from '../events/EventEmitter';
import { type Scene } from './Scene';

/**
 * Tracks which scene the engine is currently running.
 *
 * One scene is active at a time. Activating a new one destroys the outgoing scene's entities, because
 * a scene is cheap to rebuild and quietly keeping a whole game's entities alive behind a switch is the
 * more expensive mistake. Detaching without replacing is {@link SceneManager.deactivate}.
 */
export class SceneManager {
  readonly #events: EventEmitter<EngineEventMap>;

  #activeScene: Scene | undefined;

  constructor(events: EventEmitter<EngineEventMap>) {
    this.#events = events;
  }

  /** The scene systems currently run against, or undefined before the first activation. */
  get activeScene(): Scene | undefined {
    return this.#activeScene;
  }

  /** Makes `scene` active, destroying the entities of the scene it replaces. */
  activate(scene: Scene): void {
    if (scene === this.#activeScene) {
      return;
    }

    this.deactivate();
    this.#activeScene = scene;
    this.#events.emit('sceneActivated', { scene });
  }

  /** Destroys the active scene's entities and leaves the engine with no scene to run. */
  deactivate(): void {
    const previous = this.#activeScene;
    if (previous === undefined) {
      return;
    }

    this.#activeScene = undefined;
    previous.clear();
    this.#events.emit('sceneDeactivated', { scene: previous });
  }
}
